import React, { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, CheckCircle2, Clock3, AlertCircle } from 'lucide-react'
import cn from 'classnames'
import api from 'api/api'
import { toBitcoin, shortTxid } from 'lib/bsv-util'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import { subscribePaymentUpdate } from 'state/channels/useUserChannel'
import type { PaymentReceipt } from 'types/paymentReceipt'
import styles from './payment-receipts.module.scss'

export function PaymentReceiptCard({ receipt }: { receipt: PaymentReceipt }) {
    const completed = receipt.status === 'completed' && !!receipt.tx_id
    const failed = receipt.status === 'failed' || receipt.status === 'cancelled'
    const label = completed
        ? 'Payment sent'
        : failed
        ? receipt.status === 'cancelled'
            ? 'Payment cancelled'
            : 'Payment failed'
        : 'Payment processing'
    const Icon = completed ? CheckCircle2 : failed ? AlertCircle : Clock3
    const txUrl = receipt.tx_id ? bsvTxExplorerUrl(receipt.tx_id) : null

    return (
        <section
            className={styles.receipt}
            data-testid="payment-receipt"
            data-payment-status={completed ? 'completed' : failed ? receipt.status : 'pending'}
            aria-label="Payment receipt"
        >
            <div className={styles.header}>
                <span className={styles.status} aria-live="polite">
                    <Icon size={18} aria-hidden="true" />
                    {label}
                </span>
                <span className={styles.currency}>BSV</span>
            </div>
            <div className={styles.outputs}>
                {receipt.outputs.map((output, index) => (
                    <div className={styles.output} key={`${output.address}-${index}`}>
                        <span className={styles.recipient} title={output.address}>
                            {output.recipient ? `@${output.recipient}` : output.address}
                        </span>
                        <span className={styles.amount}>
                            {output.amount_sats.toLocaleString()} sats
                            <small>{toBitcoin(output.amount_sats)} BSV</small>
                        </span>
                    </div>
                ))}
            </div>
            {receipt.outputs.length > 1 && (
                <div className={styles.total}>
                    <span>{completed ? 'Total sent' : 'Total'}</span>
                    <span>{receipt.total_sats.toLocaleString()} sats</span>
                </div>
            )}
            {txUrl ? (
                <a
                    className={styles.transaction}
                    href={txUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <span>View transaction</span>
                    <span>
                        {shortTxid(receipt.tx_id)} <ArrowUpRight size={14} aria-hidden="true" />
                    </span>
                </a>
            ) : (
                <div className={styles.note}>
                    {failed
                        ? 'No completed transaction.'
                        : 'The transaction link will appear when sent.'}
                </div>
            )}
        </section>
    )
}

export default function PaymentReceipts({
    answerId,
    receipts,
    isLocal,
    replyCard,
    onLayout,
}: {
    answerId: string
    receipts: PaymentReceipt[]
    isLocal?: boolean
    replyCard?: boolean
    onLayout?: () => void
}) {
    const query = useQuery({
        queryKey: ['answer-payment-receipts', answerId],
        queryFn: () => api.getAnswerPaymentReceipts(answerId),
        initialData: receipts,
        enabled: !isLocal,
        refetchInterval: result =>
            result.state.data?.some(receipt => receipt.status === 'pending') ? 3000 : false,
    })
    const currentReceipts = query.data || receipts

    useEffect(() => {
        if (isLocal) return
        const unsubscribe = receipts.map(receipt =>
            subscribePaymentUpdate(receipt.payment_id, () => void query.refetch()),
        )
        return () => unsubscribe.forEach(stop => stop())
    }, [answerId, isLocal, receipts, query.refetch])

    useEffect(() => onLayout?.(), [currentReceipts, onLayout])

    return (
        <div
            className={cn(styles.receipts, replyCard && styles.replyCardReceipts)}
            onClick={event => event.stopPropagation()}
        >
            {currentReceipts.map(receipt => (
                <PaymentReceiptCard key={receipt.payment_id} receipt={receipt} />
            ))}
        </div>
    )
}
